"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const EventEmitter = require("wolfy87-eventemitter");
const LogManager_1 = require("../../../Log/LogManager");
const RtcCommand_1 = require("../RtcCommand");
const IRtcCommandHub_1 = require("../../Command/IRtcCommandHub");
const ProtocolConstant_1 = require("./../../../Protocol/ProtocolConstant");
const HttpApiServiceHelper_1 = require("../../../Common/HttpApiServiceHelper");
class ClientCommandHub {
    constructor(langyan) {
        this._logger = LogManager_1.logManager.GetLogger("ClientCommandHub");
        this._cmdHandlerDic = new Map();
        this._resultCmdSubject = new rxjs_1.Subject();
        this._resultCmdSubscription = null;
        this._localCmdUser = CommandUser.Nobody;
        this.errorOccurred = new EventEmitter();
        this._langyan = langyan;
    }
    get resultCmdSubject() { return this._resultCmdSubject; }
    async initAsync(localCmdUser) {
        //訂閱Langyan的channel
        await this._langyan.subscribeAsync(ClientCommandHub.getCommandChannelName(localCmdUser.name), async (rtcCmd) => this.commandChannelHandler(rtcCmd));
        //訂閱處理同步回傳的Command
        this._resultCmdSubscription = this._resultCmdSubject
            .pipe(operators_1.filter(rtcCmd => rtcCmd.isValid()))
            .subscribe({
            next: async (rtcCmd) => {
                await this.receiveCommandAsync(rtcCmd);
            },
        });
        this._localCmdUser = localCmdUser;
    }
    async terminateAsync() {
        if (this._localCmdUser === CommandUser.Nobody)
            return;
        //取消訂閱處理同步回傳的Command
        if (this._resultCmdSubscription) {
            this._resultCmdSubscription.unsubscribe();
            this._resultCmdSubscription = null;
        }
        //清掉ApplicationService註冊的CommandHandler
        this._cmdHandlerDic.clear();
        //取消在Langyan訂閱的Channel
        await this._langyan.unSubscribeAsync(ClientCommandHub.getCommandChannelName(this._localCmdUser.name));
        this.errorOccurred.removeAllListeners();
        this._localCmdUser = CommandUser.Nobody;
    }
    sendCommand(rtcCmd) {
        throw new Error("Not Implemented.");
    }
    sendCommands(rtcCmds) {
        throw new Error("Not Implemented.");
    }
    async sendCommandAsync(command) {
        let resultCmd = RtcCommand_1.RtcCommand.InvalidRtcCommand;
        try {
            if (command.transport === RtcCommand_1.TransportType.WebSync) {
                //自動補上Sender，Client端送Command，Sender一定是同一個
                if (!command.sender) {
                    // The sender of RtcCommand should always be local user.
                    const senderName = this._localCmdUser.name;
                    command = RtcCommand_1.RtcCommand.Builder(command)
                        .Sender(senderName)
                        .Build();
                }
                //this._logger.debug(`Sending command: ${command.category}.${command.name}.${command.type}.Detail=${command}`);
                this._logger.debug(`Sending command: ${command.category}.${command.name}.${command.type} to ${command.isToService ? "Service" : command.receivers.join(",")}`);
                resultCmd = await this.internalSendRtcCommandAsync(command);
            }
            else if (command.transport === RtcCommand_1.TransportType.Http) {
                resultCmd = await this.sendHttpRequestRtcCommand(command);
                this._logger.info(`Receive result command=${resultCmd.category}.${resultCmd.name}.${resultCmd.type}.${resultCmd.sender}.Id=${resultCmd.id}`);
                this._logger.debug(`Receive result command detail: ${resultCmd}`);
            }
            else {
                throw new IRtcCommandHub_1.CommandHubError("Not Implemented.");
            }
        }
        catch (error) {
            const errorMsg = `Handling ${command.category}.${command.name} command failed, reason =${error}`;
            this._logger.error(errorMsg, error);
            this.errorOccurred.emitEvent(command.category, [new IRtcCommandHub_1.CommandHubError(errorMsg, error)]);
        }
        // 統一處理回傳的Command
        // 對方可能是接收到以Blocking方式處理馬上回傳，所以回傳的Command會有對方的回應
        // 對方可能是接收到以Nonblocking處理之後才回傳，所以回傳的Command是RtcCommand.InvalidRtcCommand
        // 用Rx來收回傳的RtcCommand，就不會有Thread處理問題
        // 如果對方沒有回傳，或是回傳RtcCommand.InvalidRtcCommand，就不需處理
        if (resultCmd !== null
            && resultCmd !== RtcCommand_1.RtcCommand.InvalidRtcCommand //送給Service會回傳InvalidRtcCommand
        ) {
            //用Rx來收回傳的RtcCommand，就不會有Thread處理問題
            this._resultCmdSubject.next(new RtcCommand_1.RtcCommand(resultCmd));
        }
        return resultCmd;
    }
    async internalSendRtcCommandAsync(rtcCmd) {
        const commandJson = JSON.stringify(rtcCmd);
        if (rtcCmd.isToService) {
            const result = await this._langyan.requestToServiceAsync(ProtocolConstant_1.RTC_COMMAND_SERVICE_CHANNEL_NAME, commandJson);
            if (!result)
                return RtcCommand_1.RtcCommand.InvalidRtcCommand;
            //Server should return string.Empty of JSON, it will be deserialize to null.
            const resultRtcCmd = JSON.parse(result);
            return resultRtcCmd != null ? resultRtcCmd : RtcCommand_1.RtcCommand.InvalidRtcCommand;
        }
        else {
            //Send to receivers of Langyan.Client
            for (const receiver of rtcCmd.receivers) {
                const channelName = `${ProtocolConstant_1.RTC_CHATTING_COMMAND_CHANNEL_NAME}/${receiver}`;
                await this._langyan.publishToChannelAsync(channelName, commandJson);
            }
            return RtcCommand_1.RtcCommand.InvalidRtcCommand;
        }
    }
    async sendHttpRequestRtcCommand(rtcCmd) {
        try {
            const httpResponse = await this.sendHttpRequest(rtcCmd);
            //可以繼續表示Http協定層已經成功
            const retCmdBuilder = RtcCommand_1.RtcCommand.Builder(rtcCmd)
                .CmdTypeAction(RtcCommand_1.CmdType.ResponseAccept)
                .IsToService(false)
                .ClearReceiver()
                .Content({});
            //204 No Content
            if (httpResponse.status !== 204) {
                //送Http成功，回成功Command與結果，因為這一層不知道結果怎麼解析，所以交由上層處理
                const cmdResultJo = await httpResponse.json();
                retCmdBuilder.Content(cmdResultJo);
            }
            return retCmdBuilder.Build();
        }
        catch (err) {
            //出Exception表示Http協定層失敗，有可能是Server回傳失敗，或是網路失敗
            return RtcCommand_1.RtcCommand.Builder(rtcCmd)
                .CmdTypeAction(RtcCommand_1.CmdType.ResponseFail)
                .IsToService(false)
                .ClearReceiver()
                .Message(err.message)
                .Build();
        }
    }
    async sendHttpRequest(rtcCmd) {
        const completeUri = rtcCmd.receivers[0];
        const httpAct = rtcCmd.receivers[1].toUpperCase();
        switch (httpAct) {
            case "POST":
                {
                    this._logger.debug(`sending Http ${httpAct} to ${completeUri}`);
                    //用application/json送，比較好擴充，但送進來就要準備好JObject
                    const postContent = rtcCmd.contentTo();
                    const postContentStr = JSON.stringify(postContent);
                    this._logger.debug(`Post content: \n ${postContentStr}`);
                    const res = await HttpApiServiceHelper_1.default.postAsync(completeUri, 'application/json', postContentStr);
                    if (!res.ok)
                        throw new IRtcCommandHub_1.CommandHubError(`Failed to send ${httpAct} ${completeUri}, reason: ${res.status} ${res.statusText}`);
                    return res;
                }
            case "PATCH":
                {
                    this._logger.debug(`sending Http ${httpAct} to ${completeUri}`);
                    //用application/json送，比較好擴充，但送進來就要準備好JObject
                    const patchContent = rtcCmd.contentTo();
                    const patchContentStr = JSON.stringify(patchContent);
                    this._logger.debug(`Patch content: \n ${patchContentStr}`);
                    const res = await HttpApiServiceHelper_1.default.patchAsync(completeUri, 'application/json', patchContentStr);
                    if (!res.ok)
                        throw new IRtcCommandHub_1.CommandHubError(`Failed to send ${httpAct} ${completeUri}, reason: ${res.status} ${res.statusText}`);
                    return res;
                }
            case "GET":
                throw new Error("Not Implemented.");
            default:
                throw new Error("Not Implemented.");
        }
    }
    async sendCommandsAsync(rtcCmds) {
        const rtcCmdResults = new Array();
        for (const rtcCmd of rtcCmds) {
            const result = await this.sendCommandAsync(rtcCmd);
            rtcCmdResults.push(result);
        }
        return rtcCmdResults;
    }
    registerCommandHandler(category, handler) {
        if (!category)
            throw new IRtcCommandHub_1.CommandHubError(`category should not be empty.`);
        if (!handler)
            throw new IRtcCommandHub_1.CommandHubError(`handler should not be null`);
        this._cmdHandlerDic.set(category, handler);
        this._logger.info(`Register ${category} command handler with ${handler}`);
    }
    unRegisterCommandHandler(category) {
        if (!this._cmdHandlerDic.has(category))
            return;
        else {
            const willbeUnregister = this._cmdHandlerDic.get(category);
            this._cmdHandlerDic.delete(category);
            this._logger.info(`UnRegister ${category} command handler with ${willbeUnregister}`);
        }
    }
    hasCommandHandler(category) {
        return this._cmdHandlerDic.has(category);
    }
    countOfCommandHandler() {
        return this._cmdHandlerDic.size;
    }
    receiveCommand(rtcCmd) {
        this._logger.info(`Receive command=${rtcCmd.category}.${rtcCmd.name}.${rtcCmd.type}.${rtcCmd.sender}.Id=${rtcCmd.id}`);
        this._logger.debug(`Receive command detail: ${rtcCmd}`);
        throw new Error("Not Implemented.");
    }
    async receiveCommandAsync(rtcCmd) {
        this._logger.info(`Receive command=${rtcCmd.category}.${rtcCmd.name}.${rtcCmd.type}.${rtcCmd.sender}.Id=${rtcCmd.id}`);
        //this._logger.debug(`Receive command detail: ${rtcCmd}`);
        try {
            // TODO: Command處理從RtcService完全移走之後，就把Log與Exception Handling搬過來
            const cmdHandler = this._cmdHandlerDic.get(rtcCmd.category);
            if (cmdHandler) {
                await cmdHandler.handleCommandAsync(rtcCmd);
            }
            else {
                this._logger.warn(`No related handler for commoand ${rtcCmd.category}.${rtcCmd.name}. Something wrong??`);
            }
        }
        catch (error) {
            const errorMsg = `Handling ${rtcCmd.category}.${rtcCmd.name} command failed, reason =${error}`;
            this._logger.error(errorMsg, error);
            this.errorOccurred.emitEvent(rtcCmd.category, [new IRtcCommandHub_1.CommandHubError(errorMsg, error)]);
        }
    }
    static getCommandChannelName(username) {
        return `${ProtocolConstant_1.RTC_CHATTING_COMMAND_CHANNEL_NAME}/${username}`;
    }
    async commandChannelHandler(command) {
        //_resultCmdSubject.OnNext(command);//Using Rx to separate handling thread with Langyan.
        await this.receiveCommandAsync(new RtcCommand_1.RtcCommand(command));
    }
}
exports.ClientCommandHub = ClientCommandHub;
class CommandUser {
    constructor(id, name) {
        this.id = id;
        this.name = name;
    }
}
CommandUser.Nobody = new CommandUser("", "");
exports.CommandUser = CommandUser;
//# sourceMappingURL=ClientCommandHub.js.map